import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare, ExternalLink, RefreshCw } from "lucide-react";
import api from "../services/api";
import { formatDateTime, formatRelativeTime } from "../utils/dateFormatter";

/**
 * Session List Page
 *
 * Displays a table of all tracked chatbot sessions with:
 * - Location, City, Table Number (first 3 columns)
 * - Session ID
 * - Created timestamp
 * - Last message timestamp
 * - Number of messages
 * - Click to view full session details
 */

interface Session {
  id: string;
  createdAt: string;
  updatedAt: string;
  location: string | null;
  city: string | null;
  tableNumber: string | null;
  ipAddress: string | null;
  messageCount: number;
  lastMessageTime: string;
  lastInteractionType?: string | null;
}

const SessionListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminContext = location.pathname.startsWith("/admin");

  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const limit = 20;

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/api/sessions", {
        params: { limit, offset: (page - 1) * limit },
      });

      if (response.data.success) {
        setSessions(response.data.sessions);
        setTotalSessions(response.data.total);
      } else {
        setError("Failed to load sessions");
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError("An error occurred while loading sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [page]);

  // Date formatting now handled by utils/dateFormatter with IST timezone

  const handleViewSession = (sessionId: string) => {
    const basePath = isAdminContext ? "/admin/sessions" : "/sessions";
    navigate(`${basePath}/${sessionId}`);
  };

  const totalPages = Math.ceil(totalSessions / limit);

  return (
    <div className={isAdminContext ? "" : "min-h-screen bg-gray-50 p-4"}>
      <div className="max-w-7xl mx-auto space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Sessions</h2>
          </div>
          <button
            onClick={fetchSessions}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Stats Cards - Compact */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white rounded border border-gray-200 p-2">
            <p className="text-xs text-gray-600">Total</p>
            <p className="text-lg font-bold text-gray-900">{totalSessions}</p>
          </div>
          <div className="bg-white rounded border border-gray-200 p-2">
            <p className="text-xs text-gray-600">Active (1h)</p>
            <p className="text-lg font-bold text-gray-900">
              {
                sessions.filter((s) => {
                  const lastMsg = new Date(s.lastMessageTime);
                  const now = new Date();
                  return now.getTime() - lastMsg.getTime() < 3600000;
                }).length
              }
            </p>
          </div>
          <div className="bg-white rounded border border-gray-200 p-2">
            <p className="text-xs text-gray-600">Avg Turns</p>
            <p className="text-lg font-bold text-gray-900">
              {sessions.length > 0
                ? (sessions.reduce((sum, s) => sum + s.messageCount, 0) / sessions.length).toFixed(
                    1,
                  )
                : "0"}
            </p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-2">
            <p className="text-red-800 text-xs">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded border border-gray-200 p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 text-sm mt-2">Loading...</p>
          </div>
        )}

        {/* Sessions Table */}
        {!loading && !error && (
          <>
            <div className="bg-white rounded border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Location
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        City
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        T (Table)
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Session ID
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Created
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Last Msg
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Turns
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                          <MessageSquare className="mx-auto mb-2 text-gray-400" size={32} />
                          <p className="text-sm font-medium">No sessions found</p>
                        </td>
                      </tr>
                    ) : (
                      sessions.map((session) => (
                        <tr
                          key={session.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleViewSession(session.id)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {session.location || "-"}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {session.city || "-"}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {session.tableNumber || "-"}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs font-mono text-gray-900">
                              {session.id.substring(0, 8)}...
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {formatDateTime(session.createdAt)}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-xs text-gray-900">
                              {formatRelativeTime(session.lastMessageTime)}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                              {session.messageCount}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewSession(session.id);
                              }}
                              className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                            >
                              View
                              <ExternalLink size={12} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs">
                <div className="text-gray-700">
                  {(page - 1) * limit + 1}-{Math.min(page * limit, totalSessions)} of{" "}
                  {totalSessions}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SessionListPage;
